import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/** Notification row as returned by the SELECT in this hook. */
export interface NotificationItem {
  id: string;
  event: string;
  title: string | null;
  message: string | null;
  link: string | null;
  read: boolean;
  created_at: string | null;
}

export interface UseNotificationsResult {
  notifications: NotificationItem[];
  unreadCount: number;
  loading: boolean;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  reload: () => Promise<void>;
}

/**
 * useNotifications — fetches, subscribes to, and manages in-app notifications.
 * Mount once in a top-level component (e.g. TopBar) to avoid duplicate subscriptions.
 */
export function useNotifications(userId: string | null | undefined): UseNotificationsResult {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  // ── Initial fetch ───────────────────────────────────────────
  // Day 80 P5.5 L882/B34 — wrapped in try/catch with retry-once-at-500ms
  // to handle auth-latency cascade (TypeError: Failed to fetch on first
  // call after sign-in before the session token is ready). Empty state on
  // 2x fail is the correct UX fallback per TONE_STANDARDS §4.
  const loadNotifications = useCallback(async (): Promise<void> => {
    if (!userId) return;
    setLoading(true);
    const fetchOnce = () =>
      supabase
        .from('notifications')
        .select('id, event, title, message, link, read, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);
    try {
      let result = await fetchOnce();
      if (result.error) {
        await new Promise((r) => setTimeout(r, 500));
        result = await fetchOnce();
        if (result.error) {
          console.error('[Arbi] notifications fetch error (2x):', result.error.message);
          setLoading(false);
          return;
        }
      }
      const rows = (result.data ?? []) as unknown as NotificationItem[];
      setNotifications(rows);
      setUnreadCount(rows.filter((n) => !n.read).length);
      setLoading(false);
    } catch (e) {
      console.error(
        '[Arbi] notifications fetch threw:',
        e instanceof Error ? e.message : String(e)
      );
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // ── Realtime subscription ───────────────────────────────────
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`notifications:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const row = payload.new as NotificationItem;
          setNotifications((prev) => [row, ...prev].slice(0, 50));
          if (!row.read) setUnreadCount((prev) => prev + 1);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const row = payload.new as NotificationItem;
          setNotifications((prev) => prev.map((n) => (n.id === row.id ? row : n)));
          // Recalculate unread
          setNotifications((prev) => {
            setUnreadCount(prev.filter((n) => !n.read).length);
            return prev;
          });
        }
      )
      .subscribe((status, error) => {
        // Day 80 P5.5 L882/B34 — bare error handler. No toast (realtime
        // drop is background, too noisy to surface); fallback for the
        // user is the initial fetch + manual reload via TopBar bell.
        if (error) {
          console.error(
            '[Arbi] notifications realtime subscribe error:',
            error instanceof Error ? error.message : String(error),
            `(status=${status})`
          );
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  // ── Mark single notification as read ────────────────────────
  const markAsRead = useCallback(async (id: string): Promise<void> => {
    // Optimistic
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnreadCount((prev) => Math.max(0, prev - 1));

    const { error } = await supabase
      .from('notifications')
      .update({ read: true } as never)
      .eq('id', id);
    if (error) console.error('[Arbi] markAsRead error:', error.message);
  }, []);

  // ── Mark all as read ────────────────────────────────────────
  const markAllAsRead = useCallback(async (): Promise<void> => {
    if (!userId) return;
    // Optimistic
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);

    const { error } = await supabase
      .from('notifications')
      .update({ read: true } as never)
      .eq('user_id', userId)
      .eq('read', false);
    if (error) console.error('[Arbi] markAllAsRead error:', error.message);
  }, [userId]);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    reload: loadNotifications,
  };
}
